"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import {
  type PercentDifferenceResult,
  type PercentErrorResult,
  percentDifference,
  percentError,
} from "@/lib/tool-kit/math/percent-error";

/** English strings. A translated content file overrides these through `ui`. */
export const PERCENT_ERROR_CALCULATOR_STRINGS = {
  modeLabel: "What are you comparing?",
  modeError: "A measurement with an accepted value (percent error)",
  modeDifference: "Two measurements (percent difference)",
  experimental: "Experimental (measured) value",
  accepted: "Accepted (true or theoretical) value",
  first: "First measurement",
  second: "Second measurement",
  unit: "Unit (optional)",
  unitPlaceholder: "m/s²",
  exampleUnit: "m/s²",
  signed: "Keep the sign (+ above the accepted value, − below it)",
  decimals: "Round the percentage to",
  decimalPlaces: "one: {count} decimal place|other: {count} decimal places",
  results: "Results",
  percentError: "Percent error",
  signedPercentError: "Signed percent error",
  absoluteError: "Absolute error |E − A|",
  relativeError: "Relative error",
  percentDifference: "Percent difference",
  absoluteDifference: "Absolute difference |a − b|",
  average: "Average of the two",
  working: "The working",
  sentenceHeading: "A sentence for your lab report",
  sentenceAbove:
    "The experimental value ({experimental}) was {difference} higher than the accepted value ({accepted}), a percent error of {percent}%.",
  sentenceBelow:
    "The experimental value ({experimental}) was {difference} lower than the accepted value ({accepted}), a percent error of {percent}%.",
  sentenceExact:
    "The experimental value ({experimental}) matched the accepted value ({accepted}) exactly: a percent error of 0%.",
  sentenceDifference:
    "The two measurements ({first} and {second}) differ by {difference}, a percent difference of {percent}% of their average.",
  copySentence: "Copy sentence",
  copied: "Copied!",
  enterError:
    "Enter the experimental and the accepted value to see the percent error.",
  enterDifference: "Enter both measurements to see the percent difference.",
  acceptedZero:
    "The accepted value is 0, so percent error is undefined — it would divide by zero. Report the absolute error instead: {absolute}.",
  bothZero:
    "Both values are 0, so there is no average to compare against and percent difference is undefined.",
  differenceNote:
    "Percent difference treats both values equally — neither is assumed correct. If one of them is a known or textbook value, switch to percent error.",
  errorNote:
    "Percent error is always relative to the accepted value. To compare two of your own trials, switch to percent difference.",
} as const;

type Mode = "error" | "difference";
type Strings = typeof PERCENT_ERROR_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const DECIMAL_CHOICES = ["0", "1", "2", "3", "4"] as const;

/** Echo a typed or intermediate value without float noise (0.13, not 0.13000000000000078). */
function plain(fmt: Format, value: number): string {
  return fmt(value, { maximumSignificantDigits: 10 });
}

function withUnit(text: string, unit: string): string {
  return unit === "" ? text : `${text} ${unit}`;
}

function fixed(fmt: Format, value: number, decimals: number): string {
  return fmt(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function errorSentence(
  r: PercentErrorResult,
  strings: Strings,
  fmt: Format,
  unit: string,
  decimals: number
): string {
  const values = {
    experimental: withUnit(plain(fmt, r.experimental), unit),
    accepted: withUnit(plain(fmt, r.accepted), unit),
    difference: withUnit(plain(fmt, r.absoluteError), unit),
    percent: fixed(fmt, r.percentError, decimals),
  };
  if (r.difference === 0) {
    return t(strings.sentenceExact, values);
  }
  return t(
    r.difference > 0 ? strings.sentenceAbove : strings.sentenceBelow,
    values
  );
}

function ErrorResults({
  r,
  signed,
  decimals,
  unit,
}: {
  r: PercentErrorResult;
  signed: boolean;
  decimals: number;
  unit: string;
}) {
  const strings = useToolUi(PERCENT_ERROR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const headline = signed ? r.signedPercentError : r.percentError;
  const sign = signed && headline > 0 ? "+" : "";
  const sentence = errorSentence(r, strings, fmt, unit, decimals);
  const e = plain(fmt, r.experimental);
  const a = plain(fmt, r.accepted);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={signed ? strings.signedPercentError : strings.percentError}
          value={`${sign}${fixed(fmt, headline, decimals)}%`}
        />
        <StatCard
          label={strings.absoluteError}
          value={withUnit(plain(fmt, r.absoluteError), unit)}
        />
        <StatCard
          label={strings.relativeError}
          value={fmt(r.relativeError, { maximumSignificantDigits: 6 })}
        />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          |E − A| = |{e} − {a}| = {plain(fmt, r.absoluteError)}
        </p>
        <p>
          |E − A| / |A| = {plain(fmt, r.absoluteError)} /{" "}
          {plain(fmt, Math.abs(r.accepted))} ={" "}
          {fmt(r.relativeError, { maximumSignificantDigits: 6 })}
        </p>
        <p>
          {fmt(r.relativeError, { maximumSignificantDigits: 6 })} × 100 ={" "}
          <strong className="text-foreground">
            {fixed(fmt, r.percentError, decimals)}%
          </strong>
        </p>
        {signed ? (
          <p>
            (E − A) / |A| × 100 = {plain(fmt, r.difference)} /{" "}
            {plain(fmt, Math.abs(r.accepted))} × 100 ={" "}
            <strong className="text-foreground">
              {sign}
              {fixed(fmt, r.signedPercentError, decimals)}%
            </strong>
          </p>
        ) : null}
      </div>
      <Sentence text={sentence} />
    </>
  );
}

function DifferenceResults({
  r,
  decimals,
  unit,
}: {
  r: PercentDifferenceResult;
  decimals: number;
  unit: string;
}) {
  const strings = useToolUi(PERCENT_ERROR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const percent = fixed(fmt, r.percentDifference, decimals);
  const sentence = t(strings.sentenceDifference, {
    first: withUnit(plain(fmt, r.first), unit),
    second: withUnit(plain(fmt, r.second), unit),
    difference: withUnit(plain(fmt, r.absoluteDifference), unit),
    percent,
  });
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.percentDifference}
          value={`${percent}%`}
        />
        <StatCard
          label={strings.absoluteDifference}
          value={withUnit(plain(fmt, r.absoluteDifference), unit)}
        />
        <StatCard
          label={strings.average}
          value={withUnit(plain(fmt, r.average), unit)}
        />
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          |a − b| = |{plain(fmt, r.first)} − {plain(fmt, r.second)}| ={" "}
          {plain(fmt, r.absoluteDifference)}
        </p>
        <p>
          (|a| + |b|) / 2 = ({plain(fmt, Math.abs(r.first))} +{" "}
          {plain(fmt, Math.abs(r.second))}) / 2 = {plain(fmt, r.average)}
        </p>
        <p>
          {plain(fmt, r.absoluteDifference)} / {plain(fmt, r.average)} × 100 ={" "}
          <strong className="text-foreground">{percent}%</strong>
        </p>
      </div>
      <Sentence text={sentence} />
    </>
  );
}

function Sentence({ text }: { text: string }) {
  const strings = useToolUi(PERCENT_ERROR_CALCULATOR_STRINGS);
  return (
    <div className={`${RESULT_CARD} space-y-3`}>
      <h3 className={WIDGET_H3}>{strings.sentenceHeading}</h3>
      <p className="text-foreground text-sm leading-relaxed">{text}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copySentence}
        small
        text={text}
      />
    </div>
  );
}

/** Percent error from the two typed values, or the notice that explains why not. */
function ErrorPanel({
  experimental,
  accepted,
  signed,
  decimals,
  unit,
}: {
  experimental: string;
  accepted: string;
  signed: boolean;
  decimals: number;
  unit: string;
}) {
  const strings = useToolUi(PERCENT_ERROR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const e = parseMeasuredNumber(experimental);
  const a = parseMeasuredNumber(accepted);
  if (e === null || a === null) {
    return <Notice>{strings.enterError}</Notice>;
  }
  const result = percentError(e, a);
  if (result === null) {
    return (
      <ErrorNotice>
        {t(strings.acceptedZero, {
          absolute: withUnit(plain(fmt, Math.abs(e)), unit),
        })}
      </ErrorNotice>
    );
  }
  return (
    <ErrorResults decimals={decimals} r={result} signed={signed} unit={unit} />
  );
}

/** Percent difference from the two typed measurements, or why it is undefined. */
function DifferencePanel({
  first,
  second,
  decimals,
  unit,
}: {
  first: string;
  second: string;
  decimals: number;
  unit: string;
}) {
  const strings = useToolUi(PERCENT_ERROR_CALCULATOR_STRINGS);
  const x1 = parseMeasuredNumber(first);
  const x2 = parseMeasuredNumber(second);
  if (x1 === null || x2 === null) {
    return <Notice>{strings.enterDifference}</Notice>;
  }
  const result = percentDifference(x1, x2);
  if (result === null) {
    return <ErrorNotice>{strings.bothZero}</ErrorNotice>;
  }
  return <DifferenceResults decimals={decimals} r={result} unit={unit} />;
}

export default function PercentErrorCalculatorWidget() {
  const strings = useToolUi(PERCENT_ERROR_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("error");
  const [experimental, setExperimental] = useState("9.68");
  const [accepted, setAccepted] = useState("9.81");
  const [first, setFirst] = useState("12.4");
  const [second, setSecond] = useState("12.9");
  const [unit, setUnit] = useState<string>(strings.exampleUnit);
  const [signed, setSigned] = useState(false);
  const [decimals, setDecimals] =
    useState<(typeof DECIMAL_CHOICES)[number]>("2");

  const decimalOptions = DECIMAL_CHOICES.map((value) => ({
    value,
    label: tPlural(strings.decimalPlaces, locale, Number(value), {
      count: fmt(Number(value)),
    }).trim(),
  }));

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="pe-mode"
        onChange={setMode}
        options={[
          { value: "error", label: strings.modeError },
          { value: "difference", label: strings.modeDifference },
        ]}
        value={mode}
      />
      {mode === "error" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="pe-experimental"
            label={strings.experimental}
            numeric
            onChange={setExperimental}
            value={experimental}
          />
          <TextField
            id="pe-accepted"
            label={strings.accepted}
            numeric
            onChange={setAccepted}
            value={accepted}
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="pe-first"
            label={strings.first}
            numeric
            onChange={setFirst}
            value={first}
          />
          <TextField
            id="pe-second"
            label={strings.second}
            numeric
            onChange={setSecond}
            value={second}
          />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="pe-unit"
          label={strings.unit}
          onChange={setUnit}
          placeholder={strings.unitPlaceholder}
          value={unit}
        />
        <SelectField
          id="pe-decimals"
          label={strings.decimals}
          onChange={setDecimals}
          options={decimalOptions}
          value={decimals}
        />
      </div>
      {mode === "error" ? (
        <Checkbox
          checked={signed}
          label={strings.signed}
          onChange={setSigned}
        />
      ) : null}

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "error" ? (
          <ErrorPanel
            accepted={accepted}
            decimals={Number(decimals)}
            experimental={experimental}
            signed={signed}
            unit={unit.trim()}
          />
        ) : (
          <DifferencePanel
            decimals={Number(decimals)}
            first={first}
            second={second}
            unit={unit.trim()}
          />
        )}
        <p className="text-muted-foreground text-xs">
          {mode === "error" ? strings.errorNote : strings.differenceNote}
        </p>
      </section>
    </>
  );
}
